/**
 * CreativeLibraryPicker — A4.3.3 entry-point chooser for Create Meta Ad.
 *
 * Renders three "Create new" tiles (Link enabled; Video + Carousel
 * disabled with "Coming in A4.3.3.1" tooltips), then a grid of the
 * member's existing creatives loaded from
 * `meta_ads_data.ad_creatives_daily` via `useMetaAdCreativesQuery`.
 *
 * Designed as a standalone modal so a future ads editor can reuse it
 * without coupling to the wizard. The picker only emits an
 * `onPick(source)` callback — it doesn't open the wizard itself.
 *
 * Filtering / search are client-side. Backend returns up to ~24 per
 * account today; revisit pagination when smoke shows accounts with
 * >50 creatives.
 */
import React, { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useMetaAdCreativesQuery } from '@/hooks/queries/useMetaAdCreativesQuery';
import type {
  MetaAdCreative,
  MetaAdCreativeKind,
  MetaCreativeSource,
} from '@/services/api/metaAds';

import {
  AD_TYPE_LABELS,
  CREATIVE_KIND_FILTERS,
} from './constants/metaAdMatrix';

type FilterKind = (typeof CREATIVE_KIND_FILTERS)[number]['value'];

export interface CreativeLibraryPickerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Ad account whose creatives we list. Empty / unknown disables the
   *  existing-creatives grid (the picker still renders the "Create new"
   *  tiles). */
  metaAdAccountId: string;
  /** Caller decides what to do with the picked source. The wizard
   *  passes a callback that opens itself with the source as a prop;
   *  a future ads editor will pass its own callback. */
  onPick: (
    source: MetaCreativeSource,
    pickedCreative: MetaAdCreative | null,
  ) => void;
}

function CreateNewTile({
  type,
  disabled,
  onClick,
}: {
  type: 'link' | 'video' | 'carousel';
  disabled: boolean;
  onClick: () => void;
}): React.ReactElement {
  const { title, subtitle } = AD_TYPE_LABELS[type];
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={disabled ? 'Coming in A4.3.3.1' : undefined}
      aria-label={`Create new ${title}${disabled ? ' (coming soon)' : ''}`}
      className={
        disabled
          ? 'border-2 border-dashed border-gray-200 rounded-lg p-4 text-left cursor-not-allowed opacity-60'
          : 'border-2 border-dashed border-blue-300 rounded-lg p-4 text-left hover:border-blue-500 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
      }
    >
      <div className="text-base font-semibold text-gray-900">
        + {title}
      </div>
      <div className="text-sm text-gray-600 mt-1">{subtitle}</div>
    </button>
  );
}

function CreativeTile({
  creative,
  onClick,
}: {
  creative: MetaAdCreative;
  onClick: () => void;
}): React.ReactElement {
  const preview =
    creative.thumbnail_url || creative.image_url || null;
  return (
    <button
      onClick={onClick}
      aria-label={`Use existing creative ${creative.creative_name ?? creative.creative_id}`}
      className="border border-gray-200 rounded-lg p-3 text-left hover:border-blue-400 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      {preview ? (
        <img
          src={preview}
          alt={creative.creative_name ?? 'creative'}
          className="w-full h-32 object-cover rounded mb-2"
        />
      ) : (
        <div className="w-full h-32 bg-gray-100 rounded mb-2 flex items-center justify-center text-gray-400 text-xs">
          no preview
        </div>
      )}
      <div className="text-sm font-medium text-gray-900 truncate">
        {creative.creative_name ?? `Creative ${creative.creative_id}`}
      </div>
      <div className="text-xs text-gray-500 mt-1">
        {creative.kind === 'unknown' ? 'Image' : creative.kind} ·{' '}
        {creative.creative_id}
      </div>
    </button>
  );
}

export const CreativeLibraryPicker: React.FC<CreativeLibraryPickerProps> = ({
  isOpen,
  onClose,
  metaAdAccountId,
  onPick,
}) => {
  const [filter, setFilter] = useState<FilterKind>('all');
  const [search, setSearch] = useState('');

  const query = useMetaAdCreativesQuery(
    isOpen && metaAdAccountId ? metaAdAccountId : null,
  );

  // `useInfiniteQuery` returns `{pages: PageData[]}` — flatten across
  // every page that's been fetched so client-side filter / search work
  // against the union, not just the current page.
  const allCreatives = useMemo<MetaAdCreative[]>(() => {
    const pages = query.data?.pages ?? [];
    return pages.flatMap((p) => p.creatives);
  }, [query.data]);

  const visibleCreatives = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    return allCreatives.filter((c) => {
      if (filter !== 'all') {
        // Treat 'unknown' as 'image' for filtering — the picker tile
        // also labels unknowns as Image (the common case).
        const effective: MetaAdCreativeKind =
          c.kind === 'unknown' ? 'image' : c.kind;
        if (effective !== filter) return false;
      }
      if (searchLower) {
        const name = (c.creative_name ?? '').toLowerCase();
        if (!name.includes(searchLower)) return false;
      }
      return true;
    });
  }, [allCreatives, filter, search]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Pick a creative"
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full my-4 sm:my-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1"
          aria-label="Close picker"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 pr-8">
            Create New Ad
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Pick an existing creative or build a new one.
          </p>
        </div>

        <div className="px-6 py-6 overflow-y-auto" style={{ maxHeight: '70vh' }}>
          {/* Existing creatives grid — surfaces first so users who
              already have a creative they want to reuse don't have to
              scroll past the build-new tiles every time. */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <h3 className="text-base font-semibold text-gray-900">
                Use existing
              </h3>
              <Input
                type="search"
                placeholder="Search by name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search existing creatives"
                className="sm:w-64"
              />
            </div>

            {/* Toggle-button group, not a tab interface — these chips
                filter the grid below; there's no separate tabpanel +
                aria-controls relationship that the tabs pattern
                implies. `aria-pressed` communicates the active state
                to screen readers via the standard button toggle
                vocabulary. */}
            <div
              className="flex gap-2 mb-4"
              role="group"
              aria-label="Filter creatives by type"
            >
              {CREATIVE_KIND_FILTERS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  aria-pressed={filter === opt.value}
                  onClick={() => setFilter(opt.value)}
                  className={
                    filter === opt.value
                      ? 'px-3 py-1 rounded-full text-sm bg-blue-600 text-white'
                      : 'px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {query.isLoading && (
              <p className="text-sm text-gray-500 italic">
                Loading creatives…
              </p>
            )}
            {query.isError && (
              <Alert variant="error">
                <p className="text-sm">
                  Failed to load creatives. Try closing this picker and
                  reopening it.
                </p>
              </Alert>
            )}
            {!query.isLoading && !query.isError && visibleCreatives.length === 0 && (
              <p className="text-sm text-gray-500 italic">
                {allCreatives.length === 0
                  ? 'No existing creatives yet. Use "Build new" below.'
                  : 'No creatives match this filter.'}
              </p>
            )}

            {visibleCreatives.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {visibleCreatives.map((c) => (
                  <CreativeTile
                    key={c.creative_id}
                    creative={c}
                    onClick={() =>
                      onPick(
                        {
                          mode: 'existing',
                          creative_id: c.creative_id,
                        },
                        c,
                      )
                    }
                  />
                ))}
              </div>
            )}

            {/* "Load more" affordance — visible only when the backend
                signaled there's another page. Disabled while a fetch is
                in flight so a double-click can't fire two concurrent
                paginated requests. */}
            {query.hasNextPage && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="secondary"
                  onClick={() => query.fetchNextPage()}
                  disabled={query.isFetchingNextPage}
                >
                  {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
          </div>

          {/* Build-new tiles — secondary option, sits below the
              existing-creative grid so reuse is the path of least
              resistance. */}
          <div className="mt-8">
            <h3 className="text-base font-semibold text-gray-900 mb-3">
              Build new
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <CreateNewTile
                type="link"
                disabled={false}
                onClick={() =>
                  onPick(
                    {
                      mode: 'new',
                      spec: {
                        creative_type: 'link',
                        image_url: '',
                        headline: '',
                        primary_text: '',
                        call_to_action: 'LEARN_MORE',
                        link_url: '',
                      },
                    },
                    null,
                  )
                }
              />
              <CreateNewTile
                type="video"
                disabled={true}
                onClick={() => {
                  /* disabled */
                }}
              />
              <CreateNewTile
                type="carousel"
                disabled={true}
                onClick={() => {
                  /* disabled */
                }}
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};
